import { create } from 'zustand';
import type { ToastType, ToastMessage } from '@/components/ui/Toast';

interface ToastStore {
    toasts: ToastMessage[];
    addToast: (message: string, type?: ToastType, duration?: number) => void;
    removeToast: (id: string) => void;
    clearToasts: () => void;

    // Convenience methods
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    warning: (message: string, duration?: number) => void;
    info: (message: string, duration?: number) => void;
}

export const useToastStore = create<ToastStore>((set) => ({
    toasts: [],

    addToast: (message, type = 'info', duration = 4000) => {
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        set((state) => ({
            toasts: [...state.toasts, { id, message, type, duration }],
        }));
    },

    removeToast: (id) => {
        set((state) => ({
            toasts: state.toasts.filter((toast) => toast.id !== id),
        }));
    },

    clearToasts: () => {
        set({ toasts: [] });
    },

    // Convenience methods
    success: (message, duration) => {
        useToastStore.getState().addToast(message, 'success', duration);
    },

    error: (message, duration) => {
        useToastStore.getState().addToast(message, 'error', duration);
    },

    warning: (message, duration) => {
        useToastStore.getState().addToast(message, 'warning', duration);
    },

    info: (message, duration) => {
        useToastStore.getState().addToast(message, 'info', duration);
    },
}));

// Export convenience function for use outside React components
export const toast = {
    success: (message: string, duration?: number) => useToastStore.getState().success(message, duration),
    error: (message: string, duration?: number) => useToastStore.getState().error(message, duration),
    warning: (message: string, duration?: number) => useToastStore.getState().warning(message, duration),
    info: (message: string, duration?: number) => useToastStore.getState().info(message, duration),
};
